import React, { useState, useEffect, useCallback, useRef, memo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Star, MessageCircle, Clock, Shield, Search, Sparkles, Hand, X, CheckCircle, AlertCircle, DollarSign, Phone } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import DailySpiritualInsights from './DailySpiritualInsights';
import { supabase } from '../lib/supabase';
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import VoiceCallButton from './VoiceCallButton';
import DynamicCreditsBanner from './DynamicCreditsBanner';
import TopPromoBanner from './TopPromoBanner';

const SPECIALTIES = [
  // Reading Types
  'Tarot Readings',
  'Palm Readings',
  'Astrology Readings',
  'Psychic Readings',
  'Medium Readings',
  'Crystal Readings',
  'Angel Card Readings',
  'Dream Interpretation',
  
  // Specific Areas
  'Love & Relationships',
  'Career & Finance',
  'Life Path & Destiny',
  'Family & Children',
  'Past Lives',
  'Spirit Guides',
  'Energy Healing',
  'Chakra Balancing',
  
  // Special Abilities
  'Clairvoyance',
  'Clairaudience',
  'Clairsentience',
  'Empathy',
  'Mediumship',
  'Remote Viewing',
  'Channeling',
  'Numerology'
];

interface Advisor {
  id: string;
  display_name: string;
  specialty: string;
  rating: number | null;
  per_message_rate: number | null;
  experience_years: number | null;
  profile_image_url: string | null;
  status: string;
  bio: string | null;
  total_readings: number | null;
  voice_available: boolean | null;
  per_minute_rate: number | null;
}

interface FeatureDetail {
  title: string;
  icon: React.ReactNode;
  description: string;
  benefits: string[];
}

const features: FeatureDetail[] = [
  {
    title: "Expert Psychics",
    icon: <Star className="text-purple-600 w-8 h-8" />,
    description: "Our rigorous vetting process ensures only the most gifted and authentic psychic advisors join our platform.",
    benefits: [
      "Thorough background checks and ability verification",
      "Minimum 5 years of professional experience required",
      "Regular performance reviews and client feedback monitoring",
      "Specialized expertise in various psychic disciplines",
      "Continuous training and development programs"
    ]
  },
  {
    title: "Private Readings",
    icon: <MessageCircle className="text-purple-600 w-8 h-8" />,
    description: "Experience completely private and secure psychic consultations with end-to-end encryption.",
    benefits: [
      "End-to-end encrypted communications",
      "Strict confidentiality agreements",
      "Private reading rooms",
      "Anonymous consultation options",
      "Secure payment processing"
    ]
  }
];

function LandingPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [selectedFeature, setSelectedFeature] = useState<FeatureDetail | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [featuredAdvisors, setFeaturedAdvisors] = useState<Advisor[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedBio, setExpandedBio] = useState<string | null>(null);
  const [selectedAdvisor, setSelectedAdvisor] = useState<any>(null);
  const [currentConversationId, setCurrentConversationId] = useState<string | null>(null);
  const [waitingForResponse, setWaitingForResponse] = useState(false);
  const [showTimeoutMessage, setShowTimeoutMessage] = useState(false);
  const [freeMessagesRemaining, setFreeMessagesRemaining] = useState<number>(0);
  const [freeMessagesUsed, setFreeMessagesUsed] = useState<boolean>(false);
  const [walletBalance, setWalletBalance] = useState<number>(0);
  const channelRef = useRef<any>(null);
  const statusChannelRef = useRef<any>(null);
  const requestTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    fetchFeaturedAdvisors();
    setupRealtimeSubscription();

    if (location.state?.activeChat) {
      console.log('Found active chat in location state:', location.state.activeChat);
      setSelectedAdvisor(location.state.activeChat.advisor);
      setCurrentConversationId(location.state.activeChat.id);

      window.history.replaceState({}, document.title);
    }

    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      }

      if (channelRef.current) {
        channelRef.current.unsubscribe();
      }

      if (statusChannelRef.current) {
        statusChannelRef.current.unsubscribe();
      }

      if (requestTimeoutRef.current) {
        clearTimeout(requestTimeoutRef.current);
        requestTimeoutRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (user) {
      fetchFreeMessagesRemaining();
      fetchWalletBalance();
    }
  }, [user]);

  // Add subscription to conversation status changes
  useEffect(() => {
    if (!currentConversationId) return;

    const channel = supabase
      .channel(`conversation-${currentConversationId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'conversations',
          filter: `id=eq.${currentConversationId}`
        },
        (payload) => {
          console.log('Conversation update:', payload);
          
          if (payload.new.status === 'active') {
            // Stop notification sound
            if (audioRef.current) {
              audioRef.current.pause();
              audioRef.current.currentTime = 0;
            }
            
            // Clear timeout
            if (requestTimeoutRef.current) {
              clearTimeout(requestTimeoutRef.current);
              requestTimeoutRef.current = null;
            }
            
            // Reset waiting state
            setWaitingForResponse(false);
            setShowTimeoutMessage(false);
            
            // Navigate to chat
            navigate(`/chat/${currentConversationId}`);
          } else if (payload.new.status === 'declined') {
            if (audioRef.current) {
              audioRef.current.pause();
              audioRef.current.currentTime = 0;
            }

            if (requestTimeoutRef.current) {
              clearTimeout(requestTimeoutRef.current);
              requestTimeoutRef.current = null;
            }

            setError('Advisor declined the chat request');
            setWaitingForResponse(false);
            setShowTimeoutMessage(false);
            setCurrentConversationId(null);
          } else if (payload.new.status === 'expired') {
            if (audioRef.current) {
              audioRef.current.pause();
              audioRef.current.currentTime = 0;
            }

            if (requestTimeoutRef.current) {
              clearTimeout(requestTimeoutRef.current);
              requestTimeoutRef.current = null;
            }

            setError('Your chat request has expired. Please try again.');
            setWaitingForResponse(false);
            setShowTimeoutMessage(false);
            setCurrentConversationId(null);
          }
        }
      )
      .subscribe();

    channelRef.current = channel;
    
    // Set up a 30-second timeout for advisor response
    requestTimeoutRef.current = setTimeout(() => {
      if (waitingForResponse && currentConversationId) {
        if (audioRef.current) {
          audioRef.current.pause();
          audioRef.current.currentTime = 0;
        }
        
        setShowTimeoutMessage(true);
        setWaitingForResponse(false);
      }
    }, 30000); // 30 seconds timeout
    
    return () => {
      channel.unsubscribe();
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      }
      if (requestTimeoutRef.current) {
        clearTimeout(requestTimeoutRef.current);
        requestTimeoutRef.current = null;
      }
    };
  }, [currentConversationId, waitingForResponse, navigate]);

  const fetchFreeMessagesRemaining = async () => {
    if (!user) return;
    
    try {
      const { data, error } = await supabase
        .from('message_credits')
        .select('credits_remaining, free_messages_used')
        .eq('user_id', user.id)
        .maybeSingle();

      if (error) {
        console.error('Error fetching free messages:', error);
        return;
      }

      setFreeMessagesRemaining(data?.credits_remaining || 0);
      setFreeMessagesUsed(data?.free_messages_used || false);
      console.log('Free messages remaining:', data?.credits_remaining, 'Used:', data?.free_messages_used);
    } catch (err) {
      console.error('Error fetching free messages:', err);
    }
  };

  const fetchWalletBalance = async () => {
    if (!user) return;
    
    try {
      const { data, error } = await supabase
        .from('user_wallets')
        .select('balance')
        .eq('user_id', user.id)
        .maybeSingle();

      if (error) {
        console.error('Error fetching wallet balance:', error);
        return;
      }

      setWalletBalance(data?.balance || 0);
      console.log('Wallet balance:', data?.balance);
    } catch (err) {
      console.error('Error fetching wallet balance:', err);
    }
  };

  const setupRealtimeSubscription = () => {
    console.log('Setting up realtime subscription for advisor status changes');

    // Unsubscribe from existing channel if it exists
    if (statusChannelRef.current) {
      console.log('Unsubscribing from existing status channel');
      statusChannelRef.current.unsubscribe();
      statusChannelRef.current = null;
    }

    const advisorStatusChannel = supabase.channel('advisor-status-changes-' + Date.now())
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'profiles',
          filter: `user_type=eq.advisor`
        },
        (payload) => {
          console.log('Advisor profile update received:', payload);

          if (payload.new && payload.old && payload.new.status !== payload.old.status) {
            console.log(`Advisor ${payload.new.display_name} status changed from ${payload.old.status} to ${payload.new.status}`);

            setFeaturedAdvisors(currentAdvisors =>
              currentAdvisors.map(advisor =>
                advisor.id === payload.new.id
                  ? { ...advisor, status: payload.new.status }
                  : advisor
              )
            );
          }
        }
      )
      .subscribe((status) => {
        console.log('Subscription status:', status);
      });

    statusChannelRef.current = advisorStatusChannel;
    return advisorStatusChannel;
  };

  const fetchFeaturedAdvisors = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const { data, error: fetchError } = await supabase
        .from('profiles')
        .select(`
          id,
          display_name,
          specialty,
          bio,
          rating,
          experience_years,
          profile_image_url,
          per_message_rate,
          per_minute_rate,
          voice_available,
          status,
          total_readings
        `)
        .eq('user_type', 'advisor')
        .eq('approved', true)
        .is('admin_user', false);

      if (fetchError) throw fetchError;
      if (!data) throw new Error('No advisors found');

      const advisorsWithRatings = await Promise.all(
        data.map(async (advisor) => {
          try {
            const { data: ratingsData, error: ratingsError } = await supabase
              .from('advisor_ratings')
              .select('rating')
              .eq('advisor_id', advisor.id);
              
            if (ratingsError) {
              console.error(`Error fetching ratings for advisor ${advisor.id}:`, ratingsError);
              return advisor;
            }
            
            if (ratingsData && ratingsData.length > 0) {
              const sum = ratingsData.reduce((acc, curr) => acc + curr.rating, 0);
              const avgRating = sum / ratingsData.length;
              
              return {
                ...advisor,
                rating: advisor.rating || avgRating
              };
            }
            
            return advisor;
          } catch (err) {
            console.error(`Error processing ratings for advisor ${advisor.id}:`, err);
            return advisor;
          }
        })
      );

      // Sort advisors: online first, then offline
      const sortedAdvisors = advisorsWithRatings.sort((a, b) => {
        if (a.status === 'online' && b.status !== 'online') return -1;
        if (a.status !== 'online' && b.status === 'online') return 1;
        return 0;
      });

      setFeaturedAdvisors(sortedAdvisors);
    } catch (err) {
      console.error('Error fetching advisors:', err);
      setError('Failed to load advisors. Please try again later.');
      
      // Set default advisors on error
      setFeaturedAdvisors([
        {
          id: '1',
          display_name: "Luna Starlight",
          specialty: "Tarot & Love Readings",
          rating: 4.9,
          per_message_rate: 4.99,
          per_minute_rate: 2.99,
          voice_available: true,
          experience_years: 15,
          profile_image_url: "https://images.unsplash.com/photo-1544005313-94ddf0286df2?auto=format&fit=crop&q=80&w=400",
          status: "online",
          bio: "Specializing in love and relationship readings with tarot guidance",
          total_readings: 250
        },
        {
          id: '2',
          display_name: "Mystic River",
          specialty: "Career & Finance Readings",
          rating: 4.8,
          per_message_rate: 5.99,
          per_minute_rate: 3.99,
          voice_available: true,
          experience_years: 10,
          profile_image_url: "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?auto=format&fit=crop&q=80&w=400",
          status: "online",
          bio: "Expert in career path guidance and financial destiny readings",
          total_readings: 180
        },
        {
          id: '3',
          display_name: "Crystal Seer",
          specialty: "Spiritual Guidance & Readings",
          rating: 4.7,
          per_message_rate: 3.99,
          per_minute_rate: 1.99,
          voice_available: true,
          experience_years: 8,
          profile_image_url: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?auto=format&fit=crop&q=80&w=400",
          status: "online",
          bio: "Intuitive spiritual advisor specializing in life path guidance",
          total_readings: 120
        }
      ]);
    } finally {
      setLoading(false);
    }
  };

  const playNotificationSound = async () => {
    try {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      }

      const audio = new Audio('https://qfvdrthqushzkfjniiou.supabase.co/storage/v1/object/public/sounds//mixkit-marimba-ringtone-1359%20-%20Copy.mp3');
      audio.loop = true;
      audio.volume = 0.5;
      audioRef.current = audio;

      try {
        await audio.play();
        console.log('Marimba sound played successfully');
      } catch (primaryError) {
        console.warn('Marimba sound failed, trying fallback:', primaryError);

        const fallbackSound = 'https://qfvdrthqushzkfjniiou.supabase.co/storage/v1/object/public/sounds//mixkit-marimba-ringtone-1359%20-%20Copy.mp3';
        console.log('Trying fallback sound:', fallbackSound);
        const fallbackAudio = new Audio(fallbackSound);
        fallbackAudio.loop = true;
        fallbackAudio.volume = 0.5;
        audioRef.current = fallbackAudio;

        try {
          await fallbackAudio.play();
        } catch (fallbackError) {
          console.warn('Fallback sound also failed, trying alternative:', fallbackError);

          const alternativeSounds = [
            '/sounds/bell.mp3',
            '/sounds/chime.mp3',
            '/sounds/ding.mp3',
            '/sounds/soft.mp3'
          ];

          for (const soundPath of alternativeSounds) {
            try {
              const alternativeAudio = new Audio(soundPath);
              alternativeAudio.loop = true;
              alternativeAudio.volume = 0.5;
              audioRef.current = alternativeAudio;
              await alternativeAudio.play();
              break;
            } catch (altError) {
              console.warn(`Alternative sound ${soundPath} failed:`, altError);
              continue;
            }
          }
        }
      }
    } catch (err) {
      console.error('Error playing notification sound:', err);
    }
  };

  const sendPushNotification = async (advisorId: string, clientName: string, type: 'chat_request', retryCount = 0) => {
    try {
      console.log(`Sending push notification to advisor ${advisorId}`);
      
      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/send-push-notification`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify({
          advisorId,
          clientName,
          type
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to send push notification');
      }

      const data = await response.json();
      console.log('Push notification response:', data);
      return data.success;
    } catch (err) {
      console.error(`Push notification attempt ${retryCount + 1} failed:`, err);
      
      if (retryCount < 3) { // Assuming maxRetries is 3
        const delay = 1000 * Math.pow(2, retryCount); // Exponential backoff
        console.log(`Retrying in ${delay}ms...`);
        await new Promise(resolve => setTimeout(resolve, delay));
        return sendPushNotification(advisorId, clientName, type, retryCount + 1);
      }
      
      console.error('Max retry attempts reached for push notification');
      return false;
    }
  };

  const handleStartChat = useCallback(async (advisor: Advisor) => {
    if (!user) {
      navigate('/login', { state: { from: '/advisors' } });
      return;
    }

    if (advisor.status !== 'online') {
      if (advisor.status === 'busy') {
        setError('This advisor is currently busy with another client. Please try again later or choose another advisor.');
      } else {
        setError('This advisor is currently offline');
      }
      return;
    }

    // Check if client has free messages or enough wallet balance
    try {
      // First, check if there's already an active or pending conversation with this advisor
      const { data: existingConversation, error: existingError } = await supabase
        .from('conversations')
        .select('id, status, created_at')
        .eq('client_id', user.id)
        .eq('advisor_id', advisor.id)
        .in('status', ['active', 'pending'])
        .maybeSingle();

      if (existingError && existingError.code !== 'PGRST116') {
        console.error('Error checking existing conversations:', existingError);
        throw new Error('Failed to check existing conversations');
      }

      // If there's an existing conversation, redirect to it instead of creating a new one
      if (existingConversation) {
        console.log('Found existing conversation:', existingConversation);

        if (existingConversation.status === 'active') {
          // If it's active, navigate directly to the chat
          navigate(`/chat/${existingConversation.id}`);
          return;
        } else if (existingConversation.status === 'pending') {
          const conversationAge = Date.now() - new Date(existingConversation.created_at).getTime();
          const FIVE_MINUTES = 5 * 60 * 1000;

          if (conversationAge > FIVE_MINUTES) {
            console.log('Found old pending conversation, marking as expired');
            const { error: updateError } = await supabase
              .from('conversations')
              .update({ status: 'expired' })
              .eq('id', existingConversation.id);

            if (updateError) {
              console.error('Error expiring old conversation:', updateError);
              throw new Error('Failed to expire old conversation');
            }

            console.log('Successfully expired old conversation, will create new one');
          } else {
            // If it's pending and recent, set it as the current conversation and show waiting modal
            setCurrentConversationId(existingConversation.id);
            setWaitingForResponse(true);
            setShowTimeoutMessage(false);
            playNotificationSound();

            // Get client's display name for the notification
            const { data: clientProfile } = await supabase
              .from('profiles')
              .select('display_name')
              .eq('id', user.id)
              .single();

            const clientName = clientProfile?.display_name || user?.email || 'Anonymous';

            // Send push notification to advisor's mobile device
            await sendPushNotification(advisor.id, clientName, 'chat_request');

            return;
          }
        }
      }

      // Fetch free messages info
      const { data: messageCredits, error: creditsError } = await supabase
        .from('message_credits')
        .select('credits_remaining, free_messages_used')
        .eq('user_id', user.id)
        .single();
        
      if (creditsError && creditsError.code !== 'PGRST116') {
        console.error('Error fetching message credits:', creditsError);
        throw new Error('Failed to check message credits');
      }
      
      // Fetch wallet balance
      const { data: wallet, error: walletError } = await supabase
        .from('user_wallets')
        .select('balance')
        .eq('user_id', user.id)
        .single();
        
      if (walletError && walletError.code !== 'PGRST116') {
        console.error('Error fetching wallet:', walletError);
        throw new Error('Failed to check wallet balance');
      }
      
      const hasFreeMessages = messageCredits?.credits_remaining > 0 && !messageCredits?.free_messages_used;
      const hasEnoughBalance = (wallet?.balance || 0) >= (advisor.per_message_rate || 0);
      
      if (!hasFreeMessages && !hasEnoughBalance) {
        setError(`Insufficient funds to start a chat. Please add funds to your wallet. Message price: $${advisor.per_message_rate?.toFixed(2)}`);
        return;
      }
      
      setError(null);
      setWaitingForResponse(true);
      setShowTimeoutMessage(false);

      let newConversation;
      let insertError;

      try {
        const result = await supabase
          .from('conversations')
          .insert({
            client_id: user.id,
            advisor_id: advisor.id,
            status: 'pending'
          })
          .select()
          .single();

        newConversation = result.data;
        insertError = result.error;
      } catch (err) {
        console.error('Insert attempt failed:', err);
        insertError = err;
      }

      if (insertError) {
        console.error('Error creating conversation:', insertError);

        if (insertError.message && insertError.message.includes('duplicate key value violates unique constraint')) {
          console.log('Unique constraint violation - checking for old pending conversations');

          const { data: otherPending } = await supabase
            .from('conversations')
            .select('id, created_at')
            .eq('advisor_id', advisor.id)
            .eq('status', 'pending')
            .neq('client_id', user.id)
            .maybeSingle();

          if (otherPending) {
            const conversationAge = Date.now() - new Date(otherPending.created_at).getTime();
            const TWO_MINUTES = 2 * 60 * 1000;

            if (conversationAge > TWO_MINUTES) {
              console.log('Found and expiring old pending conversation from another client');
              await supabase
                .from('conversations')
                .update({ status: 'expired' })
                .eq('id', otherPending.id);

              const retryResult = await supabase
                .from('conversations')
                .insert({
                  client_id: user.id,
                  advisor_id: advisor.id,
                  status: 'pending'
                })
                .select()
                .single();

              newConversation = retryResult.data;
              insertError = retryResult.error;
            } else {
              throw new Error('This advisor is currently reviewing another chat request. Please try again in a moment.');
            }
          }
        }

        if (insertError) {
          if (insertError.message && insertError.message.includes('violates row-level security policy')) {
            throw new Error('Unable to start chat due to permission issues. Please try again or contact support.');
          }
          throw insertError;
        }
      }

      console.log('Created new conversation:', newConversation);

      if (!newConversation || !newConversation.id) {
        throw new Error('Failed to create conversation - no conversation data returned');
      }

      setCurrentConversationId(newConversation.id);
      playNotificationSound();

      // Get client's display name for the notification
      const { data: clientProfile, error: clientError } = await supabase
        .from('profiles')
        .select('display_name')
        .eq('id', user.id)
        .single();
        
      if (clientError) {
        console.error('Error fetching client profile:', clientError);
        // Continue with a default name if there's an error
      }
      
      const clientName = clientProfile?.display_name || user?.email || 'Anonymous';
      
      // Send push notification to advisor's mobile device
      await sendPushNotification(advisor.id, clientName, 'chat_request');

    } catch (err) {
      console.error('Error starting chat:', err);
      console.error('Error type:', typeof err);
      console.error('Error details:', err instanceof Error ? err.message : JSON.stringify(err));

      if (err instanceof Error) {
        if (err.message.includes('duplicate key value violates unique constraint')) {
          setError('Chat request already sent. Please wait for advisor response.');
        } else if (err.message.includes('advisor is currently reviewing')) {
          setError(err.message);
        } else {
          setError(err.message || 'Failed to start chat. Please try again.');
        }
      } else {
        setError('Failed to start chat. Please try again.');
      }
      setWaitingForResponse(false);
      setShowTimeoutMessage(false);
      setCurrentConversationId(null);
      
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      }
    }
  }, [user, navigate]);

  const handleStartReading = (advisor: any = null) => {
    if (user) {
      // If user is logged in, navigate to advisors page
      navigate('/advisors');
    } else {
      // If not logged in, redirect to signup
      navigate('/signup', { state: { from: '/advisors' } });
    }
  };

  const handleAdvisorClick = (advisor: any) => {
    if (user) {
      // If user is logged in, navigate to advisors page
      navigate('/advisors');
    } else {
      // If not logged in, redirect to signup
      navigate('/signup', { state: { from: '/advisors' } });
    }
  };

  const filteredSpecialties = SPECIALTIES.filter(specialty =>
    specialty.toLowerCase().includes(searchTerm.toLowerCase())
  );

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (showSuggestions && !(event.target as Element).closest('.search-container')) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showSuggestions]);

  // Set up a refresh interval to periodically check for online advisors
  useEffect(() => {
    const refreshInterval = setInterval(() => {
      fetchFeaturedAdvisors();
    }, 60000); // Refresh every minute
    
    return () => clearInterval(refreshInterval);
  }, []);

  // Slider settings
  const sliderSettings = {
    dots: true,
    infinite: featuredAdvisors.length > 3,
    speed: 500,
    slidesToShow: 3,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 5000,
    pauseOnHover: true,
    responsive: [
      {
        breakpoint: 1024,
        settings: {
          slidesToShow: 2,
          slidesToScroll: 1,
        }
      },
      {
        breakpoint: 640,
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1,
        }
      }
    ]
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-400 via-purple-500 to-indigo-600">
      {/* Dynamic Promotion Banner */}
      <TopPromoBanner />

      {/* Hero Section */}
      <header className="container mx-auto px-4 py-16 text-center text-white">
        <h1 className="text-5xl font-bold mb-6">Discover Your Destiny with Psychics Insights</h1>
        <p className="text-xl mb-8">Experience transformative palm readings, daily horoscopes, and personalized guidance from our gifted psychic advisors</p>

        {/* Dynamic Credits Banner */}
        <DynamicCreditsBanner />

        <div className="flex justify-center gap-4 mb-8">
          <div className="flex items-center gap-2">
            <Star className="text-yellow-400" />
            <span>Top Rated Psychics</span>
          </div>
          <div className="flex items-center gap-2">
            <Shield />
            <span>100% Confidential</span>
          </div>
        </div>

        {/* Reading Options */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto mb-12">
          <div 
            className="bg-white/10 rounded-lg p-6 backdrop-blur-sm hover:bg-white/20 transition-colors cursor-pointer"
            onClick={() => navigate('/palm-reading')}
          >
            <Hand className="w-12 h-12 mx-auto mb-4 text-purple-300" />
            <h2 className="text-2xl font-semibold mb-2">Palm Reading</h2>
            <p className="text-purple-200">Discover your life path with our AI-powered palm analysis</p>
          </div>
          <div 
            className="bg-white/10 rounded-lg p-6 backdrop-blur-sm hover:bg-white/20 transition-colors cursor-pointer"
            onClick={() => navigate('/horoscopes')}
          >
            <Sparkles className="w-12 h-12 mx-auto mb-4 text-purple-300" />
            <h2 className="text-2xl font-semibold mb-2">Daily Horoscope</h2>
            <p className="text-purple-200">Get your personalized daily astrological guidance</p>
          </div>
        </div>
        
        {/* Call to Action */}
        <div className="flex justify-center gap-4 mb-12">
          <button
            onClick={handleStartReading}
            className="bg-purple-600 text-white px-10 py-4 rounded-full hover:bg-purple-700 transition-all text-xl font-bold shadow-2xl hover:shadow-purple-500/50 transform hover:scale-105"
          >
            {user ? 'Start Your Reading' : 'Claim Your Free Gift'}
          </button>
        </div>
        
        {/* Search Bar */}
        <div className="max-w-2xl mx-auto relative search-container">
          <div className="relative">
            <input
              type="text"
              placeholder="Search psychic advisors by specialty..."
              className="w-full px-6 py-4 rounded-lg text-gray-800 focus:outline-none focus:ring-2 focus:ring-purple-500 bg-white shadow-lg"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setShowSuggestions(true);
              }}
              onFocus={() => setShowSuggestions(true)}
            />
            <Search className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400" />
          </div>

          {/* Search Suggestions Dropdown */}
          {showSuggestions && searchTerm && (
            <div className="absolute w-full mt-2 bg-white rounded-lg shadow-xl border border-gray-200 overflow-hidden">
              {filteredSpecialties.length > 0 ? (
                <ul className="divide-y divide-gray-100">
                  {filteredSpecialties.map((specialty, index) => (
                    <li
                      key={index}
                      className="hover:bg-purple-50 cursor-pointer transition-colors"
                      onClick={() => {
                        setSearchTerm(specialty);
                        setShowSuggestions(false);
                        handleStartReading();
                      }}
                    >
                      <div className="px-6 py-3 flex items-center">
                        <div className="bg-purple-100 p-2 rounded-full mr-4">
                          <Sparkles className="w-4 h-4 text-purple-600" />
                        </div>
                        <div>
                          <p className="text-gray-900 font-medium">{specialty}</p>
                          <p className="text-sm text-gray-500 mt-0.5">
                            Find advisors specializing in {specialty.toLowerCase()}
                          </p>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              ) : (
                <div className="px-6 py-4 text-gray-500 bg-gray-50">
                  <div className="flex items-center">
                    <AlertCircle className="w-5 h-5 mr-2 text-gray-400" />
                    <span>No specialties found matching "{searchTerm}"</span>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </header>

      {/* Featured Advisors Carousel */}
      <section className="container mx-auto px-4 py-12">
        <h2 className="text-3xl font-bold text-center text-white mb-8">Featured Psychic Advisors</h2>
        
        {loading ? (
          <div className="flex justify-center">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-white"></div>
          </div>
        ) : featuredAdvisors.length > 0 ? (
          <div className="advisor-carousel">
            <Slider {...sliderSettings}>
              {featuredAdvisors.map((advisor) => (
                <div key={advisor.id} className="px-2">
                  <div className="bg-white rounded-lg shadow-xl overflow-hidden transform transition-all duration-300 hover:scale-105 flex flex-col" style={{ height: '680px' }}>
                    <div className="relative bg-gray-100 flex-shrink-0">
                      <img
                        src={advisor.profile_image_url || 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?auto=format&fit=crop&w=400&h=250'}
                        alt={advisor.display_name}
                        className="w-full h-64 object-contain"
                      />
                      <div className={`absolute top-4 right-4 px-3 py-1 rounded-full ${
                        advisor.status === 'online' ? 'bg-green-500' : 
                        advisor.status === 'busy' ? 'bg-orange-500' : 
                        'bg-gray-500'
                      } text-white text-sm`}>
                        {advisor.status === 'online' ? 'Online' : 
                         advisor.status === 'busy' ? 'Busy' : 
                         'Offline'}
                      </div>
                    </div>
                    <div className="p-6 flex flex-col flex-1 overflow-y-auto">
                      <h3 className="text-xl font-semibold mb-2">{advisor.display_name}</h3>
                      <p className="text-purple-600 mb-2">{advisor.specialty}</p>
                      
                      <div className="flex items-center mb-2">
                        <div className="flex items-center">
                          {[1, 2, 3, 4, 5].map((star) => (
                            <Star 
                              key={star}
                              className={`w-4 h-4 ${
                                star <= Math.round(advisor.rating || 0)
                                  ? 'text-yellow-400 fill-yellow-400'
                                  : 'text-gray-300'
                              }`}
                            />
                          ))}
                        </div>
                        <span className="ml-1">{advisor.rating ? advisor.rating.toFixed(1) : '0.0'}</span>
                        <Clock className="ml-4 w-5 h-5 text-gray-500" />
                        <span className="ml-1">{advisor.experience_years || 0} years</span>
                      </div>
                      
                      <div className="flex items-center mb-2 text-sm text-gray-600">
                        <span className="mr-1">Total Readings:</span>
                        <span className="font-medium">{advisor.total_readings || 0}</span>
                      </div>
                      
                      {expandedBio === advisor.id ? (
                        <div className="mb-4">
                          <p className="text-gray-600">{advisor.bio || 'Experienced psychic advisor ready to provide guidance and insights.'}</p>
                          <button 
                            onClick={(e) => {
                              e.stopPropagation();
                              setExpandedBio(null);
                            }}
                            className="text-purple-600 text-sm mt-1 hover:text-purple-800"
                          >
                            Show less
                          </button>
                        </div>
                      ) : (
                        <div className="mb-4">
                          <p className="text-gray-600 line-clamp-2">
                            {advisor.bio || 'Experienced psychic advisor ready to provide guidance and insights.'}
                          </p>
                          {advisor.bio && advisor.bio.length > 100 && (
                            <button 
                              onClick={(e) => {
                                e.stopPropagation();
                                setExpandedBio(advisor.id);
                              }}
                              className="text-purple-600 text-sm mt-1 hover:text-purple-800"
                            >
                              Read more
                            </button>
                          )}
                        </div>
                      )}

                      <div className="flex flex-col gap-2 mt-auto">
                        <div className="flex justify-between items-center">
                          <span className="text-gray-600">Price per message:</span>
                          <span className="text-xl font-bold">${(advisor.per_message_rate || 0).toFixed(2)}</span>
                        </div>
                        
                        <button 
                          onClick={() => handleStartChat(advisor)}
                          disabled={advisor.status !== 'online' || (user && !freeMessagesRemaining && !freeMessagesUsed && walletBalance < (advisor.per_message_rate || 0))}
                          className={`${
                            advisor.status === 'online'
                              ? user 
                                ? freeMessagesRemaining > 0 && !freeMessagesUsed
                                  ? 'bg-purple-600 hover:bg-purple-700'
                                  : walletBalance >= (advisor.per_message_rate || 0)
                                    ? 'bg-purple-600 hover:bg-purple-700'
                                    : 'bg-gray-400 cursor-not-allowed'
                                : 'bg-purple-600 hover:bg-purple-700'
                              : advisor.status === 'busy'
                                ? 'bg-orange-400 cursor-not-allowed'
                                : 'bg-gray-400 cursor-not-allowed'
                          } text-white px-4 py-2 rounded-full transition-colors flex items-center justify-center`}
                        >
                          <MessageCircle className="w-5 h-5 mr-2" />
                          {advisor.status === 'online'
                            ? (user
                                ? (freeMessagesRemaining > 0 && !freeMessagesUsed
                                    ? `Start Chat (${freeMessagesRemaining} free)`
                                    : walletBalance >= (advisor.per_message_rate || 0)
                                      ? 'Start Chat'
                                      : 'Add Funds to Chat')
                                : 'Sign Up to Chat')
                            : advisor.status === 'busy'
                              ? 'Advisor Busy'
                              : 'Offline'}
                        </button>
                        
                        {user && advisor.status === 'online' && freeMessagesRemaining <= 0 && walletBalance < (advisor.per_message_rate || 0) && (
                          <button
                            onClick={() => navigate('/client/dashboard')}
                            className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-full transition-colors flex items-center justify-center mt-2"
                          >
                            <DollarSign className="w-5 h-5 mr-2" />
                            Add Funds
                          </button>
                        )}
                      </div>
                      
                      {advisor.voice_available && advisor.per_minute_rate && (
                        <div className="mt-3">
                          <div className="flex justify-between items-center mb-2">
                            <span className="text-gray-600 text-sm">Voice call per minute:</span>
                            <span className="text-lg font-bold">${(advisor.per_minute_rate || 0).toFixed(2)}</span>
                          </div>
                          {user ? (
                            <VoiceCallButton
                              advisorId={advisor.id}
                              advisorName={advisor.display_name}
                              perMinuteRate={advisor.per_minute_rate || 0}
                              isVoiceAvailable={advisor.voice_available && advisor.status === 'online'}
                              onCallEnd={() => {
                                console.log(`Voice call ended with ${advisor.display_name}`);
                              }}
                            />
                          ) : (
                            <button
                              onClick={() => navigate('/signup', { state: { from: '/' } })}
                              className="w-full bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-full transition-colors flex items-center justify-center"
                            >
                              <Phone className="w-5 h-5 mr-2" />
                              Sign Up to Call
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </Slider>
          </div>
        ) : (
          <div className="text-center text-white">
            <p className="text-xl">No advisors are currently available</p>
            <button
              onClick={() => navigate('/advisors')}
              className="mt-4 bg-purple-600 text-white px-6 py-2 rounded-lg hover:bg-purple-700 transition-colors"
            >
              Browse All Advisors
            </button>
          </div>
        )}
        
        {/* View All Advisors Button */}
        <div className="text-center mt-8">
          <button
            onClick={() => navigate('/advisors')}
            className="bg-white text-purple-600 px-8 py-3 rounded-full hover:bg-gray-100 transition-colors text-lg font-semibold"
          >
            View All Advisors
          </button>
        </div>
      </section>

      {/* Daily Spiritual Insights Section */}
      <div className="bg-white">
        <DailySpiritualInsights />
      </div>

      {/* Features Section */}
      <section className="bg-white py-16">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-12">Why Choose Our Psychic Readings?</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {features.map((feature, index) => (
              <div
                key={index}
                onClick={() => setSelectedFeature(feature)}
                className="text-center cursor-pointer transform transition-all duration-300 hover:scale-105"
              >
                <div className="bg-purple-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-semibold mb-2">{feature.title}</h3>
                <p className="text-gray-600">{feature.description}</p>
                <button className="mt-4 text-purple-600 hover:text-purple-700 font-medium flex items-center justify-center mx-auto">
                  Learn More
                  <svg className="w-4 h-4 ml-2" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <path d="M5 12h14M12 5l7 7-7 7" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Feature Detail Modal */}
        {selectedFeature && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full">
              <div className="p-6 border-b relative">
                <button
                  onClick={() => setSelectedFeature(null)}
                  className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
                >
                  <X className="w-6 h-6" />
                </button>
                <div className="flex items-center mb-4">
                  <div className="bg-purple-100 w-12 h-12 rounded-full flex items-center justify-center mr-4">
                    {selectedFeature.icon}
                  </div>
                  <h3 className="text-2xl font-bold">{selectedFeature.title}</h3>
                </div>
                <p className="text-gray-600">{selectedFeature.description}</p>
              </div>
              
              <div className="p-6">
                <h4 className="text-lg font-semibold mb-4">Key Benefits:</h4>
                <ul className="space-y-3">
                  {selectedFeature.benefits.map((benefit, index) => (
                    <li key={index} className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-green-500 mr-3 flex-shrink-0 mt-0.5" />
                      <span>{benefit}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="p-6 bg-purple-50 rounded-b-lg">
                <div className="flex justify-between items-center">
                  <p className="text-purple-800 font-medium">
                    Ready to experience the difference?
                  </p>
                  <button
                    onClick={() => {
                      setSelectedFeature(null);
                      handleStartReading();
                    }}
                    className="bg-purple-600 text-white px-6 py-2 rounded-full hover:bg-purple-700 transition-colors"
                  >
                    Get Started
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </section>

      {/* Legal Disclaimer */}
      <div className="bg-gray-900 text-gray-400 py-6">
        <div className="container mx-auto px-4 text-center text-sm">
          <p className="mb-2">
            For entertainment purposes only. Must be 18 years or older to use our services.
          </p>
          <p>
            Psychic readings are not a substitute for professional medical, legal, or financial advice.
          </p>
        </div>
      </div>

      {/* Waiting for Response Modal */}
      {waitingForResponse && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl p-8 max-w-md w-full text-center">
            <h2 className="text-2xl font-bold mb-4">Connecting to Advisor</h2>
            <p className="text-gray-600 mb-4">
              Your chat request has been sent. Please wait while the advisor responds.
            </p>
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto"></div>
            <button
              onClick={() => {
                setWaitingForResponse(false);
                setShowTimeoutMessage(false);
                setCurrentConversationId(null);
                
                if (audioRef.current) {
                  audioRef.current.pause();
                  audioRef.current.currentTime = 0;
                }
                
                if (requestTimeoutRef.current) {
                  clearTimeout(requestTimeoutRef.current);
                  requestTimeoutRef.current = null;
                }
              }}
              className="mt-6 text-gray-600 hover:text-gray-800"
            >
              Cancel Request
            </button>
          </div>
        </div>
      )}

      {/* Timeout Message Modal */}
      {showTimeoutMessage && !waitingForResponse && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl p-8 max-w-md w-full text-center">
            <AlertCircle className="h-16 w-16 text-yellow-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold mb-4">No Response</h2>
            <p className="text-gray-600 mb-6">
              This advisor didn't answer. Please try another advisor or check back in a few minutes.
            </p>
            <button
              onClick={() => {
                setShowTimeoutMessage(false);
                setCurrentConversationId(null);
                if (requestTimeoutRef.current) {
                  clearTimeout(requestTimeoutRef.current);
                  requestTimeoutRef.current = null;
                }
              }}
              className="bg-purple-600 text-white px-6 py-2 rounded-lg hover:bg-purple-700 transition-colors"
            >
              Browse Advisors
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default LandingPage;